import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Briefcase, TrendingUp, Wallet, Search, Download, FileText, FileSpreadsheet, Building2, Clock, AlertTriangle, CheckCircle, ImageIcon, Home, Eye, ArrowLeft, Users, Menu, KeyRound } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(value);
};

const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  if (!isNaN(dateStr) && Number(dateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + Number(dateStr) * 86400000);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Pastel colors for pie chart matching BondMaster theme
const CHART_COLORS = ['#1B4D3E', '#D4AF37', '#3D7A5A', '#E8C547', '#5A9A7A', '#F0D978', '#7BB99A', '#F5E6A3'];

// Calculate maturity status and days remaining
const getMaturityStatus = (maturityDateStr) => {
  if (!maturityDateStr) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  
  let maturityDate;
  // Handle Excel date number format
  if (!isNaN(maturityDateStr) && Number(maturityDateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    maturityDate = new Date(excelEpoch.getTime() + Number(maturityDateStr) * 86400000);
  } else {
    maturityDate = new Date(maturityDateStr);
  }
  
  if (isNaN(maturityDate.getTime())) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  maturityDate.setHours(0, 0, 0, 0);
  
  const diffTime = maturityDate - today;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const formattedDate = maturityDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  
  if (daysRemaining < 0) {
    return { status: 'matured', daysRemaining, label: formattedDate, tooltip: 'Already matured' };
  } else if (daysRemaining < 30) {
    return { status: 'critical', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  } else if (daysRemaining < 60) {
    return { status: 'warning', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  } else if (daysRemaining < 90) {
    return { status: 'upcoming', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  } else {
    return { status: 'safe', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  }
};

// Maturity Badge Component
const MaturityBadge = ({ maturityDate, maturityPct1, maturityDate2, maturityPct2, maturityDate3, maturityPct3 }) => {
  const hasMulti = maturityPct2 || maturityPct3;
  if (hasMulti) {
    const parts = [];
    const fmtDt = (d) => { try { const dt = new Date(d); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
    if (maturityDate) parts.push(`${maturityPct1 || 100}% - ${fmtDt(maturityDate)}`);
    if (maturityPct2 && maturityDate2) parts.push(`${maturityPct2}% - ${fmtDt(maturityDate2)}`);
    if (maturityPct3 && maturityDate3) parts.push(`${maturityPct3}% - ${fmtDt(maturityDate3)}`);
    return <span className="text-xs leading-relaxed">{parts.join(', ')}</span>;
  }
  const { status, label, tooltip, daysRemaining } = getMaturityStatus(maturityDate);
  
  const statusStyles = {
    matured: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', icon: CheckCircle },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle },
    warning: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Clock },
    upcoming: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock },
    safe: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
    unknown: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', icon: Clock }
  };
  
  const style = statusStyles[status];
  const Icon = style.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`} title={tooltip}>
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      {status === 'critical' && daysRemaining !== null && (
        <span className="ml-1 px-1.5 py-0.5 bg-red-100 rounded text-[10px] font-bold">{daysRemaining}d</span>
      )}
      {status === 'warning' && daysRemaining !== null && (
        <span className="ml-1 px-1.5 py-0.5 bg-orange-100 rounded text-[10px] font-bold">{daysRemaining}d</span>
      )}
    </div>
  );
};

const SummaryCards = ({ summary, colors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
    <Card className="summary-card border-0" data-testid="summary-invested-value">
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
            <Wallet className="h-6 w-6" style={{ color: colors.primary }} />
          </div>
          <div>
            <p className="text-sm" style={{ color: colors.text_secondary }}>Total Invested Value</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>{formatCurrency(summary.total_invested_value)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card className="summary-card border-0" data-testid="summary-holdings-count">
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${colors.accent}20` }}>
            <Briefcase className="h-6 w-6" style={{ color: colors.accent }} />
          </div>
          <div>
            <p className="text-sm" style={{ color: colors.text_secondary }}>Active Holdings</p>
            <p className="text-2xl font-bold" style={{ color: colors.accent }}>{summary.total_holdings}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const HoldingsPieChart = ({ holdings, colors }) => {
  const chartData = holdings.map((h, idx) => ({
    name: h.bond_name.length > 25 ? h.bond_name.substring(0, 25) + '...' : h.bond_name,
    value: h.invested_value,
    fullName: h.bond_name
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-medium text-sm" style={{ color: colors.primary }}>{payload[0].payload.fullName}</p>
          <p className="font-semibold" style={{ color: colors.accent }}>{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Generate chart colors based on primary and accent
  const dynamicChartColors = [colors.primary, colors.accent, `${colors.primary}CC`, `${colors.accent}CC`, `${colors.primary}99`, `${colors.accent}99`, `${colors.primary}66`, `${colors.accent}66`];

  if (holdings.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm mb-8">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>Portfolio Allocation</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={100} fill="#8884d8" dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={dynamicChartColors[index % dynamicChartColors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend layout="vertical" align="right" verticalAlign="middle" formatter={(value) => <span className="text-sm text-[#475569]">{value}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const HoldingsTable = ({ holdings, onViewTransactions, isActive = true, colors }) => (
  <Card className="holdings-card border-0">
    <CardHeader className="border-b" style={{ backgroundColor: isActive ? colors.card_bg : `${colors.accent}15` }}>
      <CardTitle className="text-lg font-heading" style={{ color: isActive ? colors.primary : colors.accent }}>
        {isActive ? 'Bond Portfolio Holdings' : 'Fully Redeemed Bond Holdings'}
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <Table className="holdings-table">
          <TableHeader>
            <TableRow style={{ backgroundColor: isActive ? `${colors.light_bg}80` : `${colors.accent}15` }}>
              <TableHead className="font-semibold" style={{ color: isActive ? colors.primary : colors.accent }}>Bond ISIN</TableHead>
              <TableHead className="font-semibold" style={{ color: isActive ? colors.primary : colors.accent }}>Bond Name</TableHead>
              <TableHead className="font-semibold text-right" style={{ color: isActive ? colors.primary : colors.accent }}>Net Quantity</TableHead>
              {isActive && <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Invested Value</TableHead>}
              {isActive && <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Maturity Date</TableHead>}
              <TableHead className="font-semibold text-center" style={{ color: isActive ? colors.primary : colors.accent }}>Transactions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holdings.map((holding, idx) => (
              <TableRow key={holding.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                <TableCell className="font-mono text-sm">{holding.bond_isin}</TableCell>
                <TableCell className="font-medium">{holding.bond_name}</TableCell>
                <TableCell className={`text-right font-semibold`} style={{ color: isActive ? colors.primary : '#94a3b8' }}>{holding.net_quantity}</TableCell>
                {isActive && <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(holding.invested_value)}</TableCell>}
                {isActive && <TableCell className="text-center"><MaturityBadge maturityDate={holding.maturity_date} maturityPct1={holding.maturity_pct_1} maturityDate2={holding.maturity_date_2} maturityPct2={holding.maturity_pct_2} maturityDate3={holding.maturity_date_3} maturityPct3={holding.maturity_pct_3} /></TableCell>}
                <TableCell className="text-center">
                  <button className="lens-btn" onClick={() => onViewTransactions(holding)} data-testid={`view-transactions-${holding.bond_isin}`}>
                    <Search className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {holdings.length === 0 && (
              <TableRow>
                <TableCell colSpan={isActive ? 6 : 4} className="text-center py-8" style={{ color: colors.text_secondary }}>
                  {isActive ? 'No active holdings found' : 'No fully redeemed holdings'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
);

const TransactionModal = ({ show, onClose, holding, transactions, colors, onPlaceRedeemRequest, hasEligibleTxn }) => (
  <Dialog open={show} onOpenChange={onClose}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading" style={{ color: colors.primary }}>Transaction Details</DialogTitle>
        {holding && <p className="text-sm" style={{ color: colors.text_secondary }}>{holding.bond_name} ({holding.bond_isin})</p>}
      </DialogHeader>
      <div className="space-y-6 mt-4">
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.primary }}>
            <TrendingUp className="h-4 w-4" />Investment Transactions
          </h3>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                  <TableHead style={{ color: colors.primary }}>Transaction Date</TableHead>
                  <TableHead style={{ color: colors.primary }}>Transaction ID</TableHead>
                  <TableHead className="text-right" style={{ color: colors.primary }}>Quantity</TableHead>
                  <TableHead className="text-right" style={{ color: colors.primary }}>Invested Value</TableHead>
                  <TableHead className="text-right" style={{ color: colors.primary }}>Yield</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.investments.map((txn) => (
                  <TableRow key={txn.transaction_id}>
                    <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell>
                    <TableCell className="text-right">{txn.quantity}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(txn.invested_value)}</TableCell>
                    <TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell>
                  </TableRow>
                ))}
                {transactions.investments.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No investment transactions</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: colors.accent }}>
              <Download className="h-4 w-4" />Redemption Transactions
            </h3>
            {hasEligibleTxn && (
              <Button size="sm" className="text-white" style={{ backgroundColor: colors.accent }} onClick={() => onPlaceRedeemRequest(holding)} data-testid="place-redeem-request-btn">
                Place Redeem Request
              </Button>
            )}
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: `${colors.accent}15` }}>
                  <TableHead style={{ color: colors.accent }}>Transaction Date</TableHead>
                  <TableHead style={{ color: colors.accent }}>Transaction ID</TableHead>
                  <TableHead className="text-right" style={{ color: colors.accent }}>Quantity</TableHead>
                  <TableHead className="text-right" style={{ color: colors.accent }}>Redemption Value</TableHead>
                  <TableHead className="text-right" style={{ color: colors.accent }}>Yield</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.redemptions.map((txn) => (
                  <TableRow key={txn.transaction_id}>
                    <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell>
                    <TableCell className="text-right">{txn.quantity}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(txn.redemption_value)}</TableCell>
                    <TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell>
                  </TableRow>
                ))}
                {transactions.redemptions.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No redemption transactions</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const ClientDashboard = () => {
  const { user, logout, getAuthHeaders, branding, colorConfig } = useAuth();
  const [activeTab, setActiveTab] = useState(null); // set after families load
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [holdings, setHoldings] = useState([]);
  const [redeemedHoldings, setRedeemedHoldings] = useState([]);
  const [summary, setSummary] = useState({ total_invested_value: 0, total_holdings: 0, total_face_value: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [transactions, setTransactions] = useState({ investments: [], redemptions: [] });
  const [showTransactions, setShowTransactions] = useState(false);
  const [hideCurated, setHideCurated] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  // Curated Bonds state
  const [curatedBonds, setCuratedBonds] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(false);
  const [partnerMobile, setPartnerMobile] = useState(null);
  const [showBondImagePreview, setShowBondImagePreview] = useState(false);
  const [previewBondImage, setPreviewBondImage] = useState('');
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [enquiryBond, setEnquiryBond] = useState(null);
  const [enquiryAmount, setEnquiryAmount] = useState('');
  const [sendingEnquiry, setSendingEnquiry] = useState(false);

  // Redeem Request state
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [redeemBond, setRedeemBond] = useState(null);
  const [redeemQty, setRedeemQty] = useState('');
  const [sendingRedeem, setSendingRedeem] = useState(false);

  // Family State
  const [families, setFamilies] = useState([]);
  const [viewingMember, setViewingMember] = useState(null);
  const [memberHoldings, setMemberHoldings] = useState([]);
  const [memberSummary, setMemberSummary] = useState(null);
  const [memberTransactions, setMemberTransactions] = useState({ investments: [], redemptions: [] });
  const [showMemberTransactions, setShowMemberTransactions] = useState(false);
  const [selectedMemberHolding, setSelectedMemberHolding] = useState(null);
  const isFamilyHead = families.length > 0;

  // Memoize family pie chart data
  const familyPieData = useMemo(() =>
    families.flatMap(f => f.members?.map(m => ({ name: m.client_name?.length > 15 ? m.client_name.substring(0, 15) + '...' : m.client_name, value: m.total_invested_value || 0, fullName: m.client_name })) || []),
    [families]
  );
  const familyMembersList = useMemo(() => families.flatMap(f => f.members || []), [families]);

  // Use partner logo if available, otherwise use BondMaster logo
  const logoUrl = branding?.partner_logo_url || "https://bondmaster.com.in/logoBond.png";
  
  // Dynamic colors from config
  const colors = colorConfig || {
    primary: "#1B4D3E",
    primary_hover: "#163d32",
    accent: "#D4AF37",
    light_bg: "#E8F5E9",
    card_bg: "#F8FAF9",
    text_primary: "#1B4D3E",
    text_secondary: "#64748b"
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [holdingsRes, redeemedRes, summaryRes, familiesRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/portfolio`, getAuthHeaders()),
        axios.get(`${API_URL}/api/client/redeemed`, getAuthHeaders()),
        axios.get(`${API_URL}/api/client/summary`, getAuthHeaders()),
        axios.get(`${API_URL}/api/client/families`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/client/config`, getAuthHeaders()).catch(() => ({ data: {} }))
      ]);
      setHoldings(holdingsRes.data);
      setRedeemedHoldings(redeemedRes.data);
      setSummary(summaryRes.data);
      setFamilies(familiesRes.data || []);
      setHideCurated(configRes.data?.hide_curated_from_clients || false);
    } catch (error) {
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Set partner favicon and title if available
  useEffect(() => {
    const faviconUrl = branding?.partner_favicon_url;
    if (faviconUrl) {
      const name = branding?.partner_name || 'BondMaster';
      document.title = `${name} | Portfolio Dashboard`;
      const setLink = (rel, href, sizes) => {
        let sel = `link[rel="${rel}"]`;
        if (sizes) sel += `[sizes="${sizes}"]`;
        let el = document.querySelector(sel);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); if (sizes) el.setAttribute('sizes', sizes); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      setLink('icon', faviconUrl);
      setLink('shortcut icon', faviconUrl);
      setLink('apple-touch-icon', faviconUrl);
      setLink('apple-touch-icon', faviconUrl, '180x180');
    }
    return () => {
      document.title = 'BondMaster | Bond Portfolio Management Platform';
      const def = 'https://bondmaster.com.in/logoBond.png';
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const el = document.querySelector(`link[rel="${rel}"]`);
        if (el) el.setAttribute('href', def);
      });
    };
  }, [branding?.partner_favicon_url, branding?.partner_name]);

  // Set initial active tab after families load
  useEffect(() => {
    if (!loading && activeTab === null) {
      setActiveTab(families.length > 0 ? 'family' : 'active');
    }
  }, [loading, families.length, activeTab]);

  const viewTransactions = async (holding) => {
    try {
      const response = await axios.get(`${API_URL}/api/client/transactions/${encodeURIComponent(holding.bond_isin)}`, getAuthHeaders());
      setTransactions(response.data);
      setSelectedHolding(holding);
      setShowTransactions(true);
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  const fetchCuratedBonds = async () => {
    try {
      setLoadingCurated(true);
      const response = await axios.get(`${API_URL}/api/curated-bonds`, getAuthHeaders());
      setCuratedBonds(response.data || []);
    } catch (error) { toast.error('Failed to load curated bonds'); }
    finally { setLoadingCurated(false); }
  };

  const viewMemberPortfolio = async (member) => {
    try {
      const [holdingsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/family-member/${member.client_id}/portfolio`, getAuthHeaders()),
        axios.get(`${API_URL}/api/client/family-member/${member.client_id}/summary`, getAuthHeaders())
      ]);
      setViewingMember(member);
      setMemberHoldings(holdingsRes.data || []);
      setMemberSummary(summaryRes.data);
    } catch (error) { toast.error('Failed to load member portfolio'); }
  };

  const viewMemberTransactions = async (holding) => {
    try {
      const response = await axios.get(`${API_URL}/api/client/family-member/${viewingMember.client_id}/transactions/${encodeURIComponent(holding.bond_isin)}`, getAuthHeaders());
      setMemberTransactions(response.data);
      setSelectedMemberHolding(holding);
      setShowMemberTransactions(true);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const fetchPartnerMobile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/partner-mobile`, getAuthHeaders());
      setPartnerMobile(response.data.mobile);
    } catch (error) { /* partner mobile fetch is non-critical */ }
  };

  const handleEnquiry = () => {
    if (!enquiryAmount || isNaN(enquiryAmount) || Number(enquiryAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSendingEnquiry(true);
    const message = `I am interesting in investing in ${enquiryBond.bond_name} around INR ${Number(enquiryAmount).toLocaleString('en-IN')} . Pls confirm exact amount.`;
    const phone = partnerMobile ? `91${partnerMobile}` : '918310791241';
    const waUrl = `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(message)}`;
    setTimeout(() => {
      setSendingEnquiry(false);
      setShowEnquiryDialog(false);
      setEnquiryAmount('');
      window.open(waUrl, '_blank');
    }, 1000);
  };

  const handlePlaceRedeemRequest = (holding) => {
    setRedeemBond(holding);
    setRedeemQty('');
    setSendingRedeem(false);
    setShowRedeemDialog(true);
  };

  const handleSubmitRedeem = () => {
    if (!redeemQty || isNaN(redeemQty) || Number(redeemQty) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    setSendingRedeem(true);
    const message = `I wish to redeem ${redeemBond.bond_name} Qty ${Number(redeemQty)} . Pls guide on the next steps.`;
    const phone = partnerMobile ? `91${partnerMobile}` : '918310791241';
    const waUrl = `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(message)}`;
    setTimeout(() => {
      setSendingRedeem(false);
      setShowRedeemDialog(false);
      setRedeemQty('');
      window.open(waUrl, '_blank');
    }, 1000);
  };

  const hasEligibleTxnForRedeem = () => {
    if (!transactions || !transactions.investments) return false;
    const allTxnIds = [...transactions.investments.map(t => t.transaction_id), ...transactions.redemptions.map(t => t.transaction_id)];
    return allTxnIds.some(id => String(id).startsWith('20'));
  };

  const exportToPDF = async () => {
    try {
      toast.info('Generating PDF with transactions...');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      doc.setFontSize(20);
      doc.setTextColor(27, 77, 62);
      doc.text('Bond Portfolio Report', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(user.entity_name || user.username, pageWidth / 2, 28, { align: 'center' });
      doc.text('Generated: ' + new Date().toLocaleDateString('en-IN'), pageWidth / 2, 35, { align: 'center' });
      doc.setFontSize(14);
      doc.setTextColor(27, 77, 62);
      doc.text('Portfolio Summary', 14, 50);
      doc.setFontSize(10);
      doc.setTextColor(60);
      doc.text('Total Invested Value: ' + formatCurrency(summary.total_invested_value), 14, 58);
      doc.text('Total Holdings: ' + summary.total_holdings, 14, 65);
      
      let currentY = 80;
      
      // For each holding, add details and transactions
      for (const holding of holdings) {
        // Check if we need a new page
        if (currentY > 250) {
          doc.addPage();
          currentY = 20;
        }
        
        // Holding header
        doc.setFontSize(12);
        doc.setTextColor(27, 77, 62);
        doc.text(holding.bond_name, 14, currentY);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text('ISIN: ' + holding.bond_isin + ' | Net Qty: ' + holding.net_quantity + ' | Invested: ' + formatCurrency(holding.invested_value), 14, currentY + 6);
        currentY += 12;
        
        // Fetch transactions for this holding
        try {
          const txnResponse = await axios.get(API_URL + '/api/client/transactions/' + encodeURIComponent(holding.bond_isin), getAuthHeaders());
          const txnData = txnResponse.data;
          
          // Investment transactions
          if (txnData.investments && txnData.investments.length > 0) {
            autoTable(doc, {
              startY: currentY,
              head: [['Date', 'Transaction ID', 'Qty', 'Invested Value', 'Yield']],
              body: txnData.investments.map(t => [
                formatDate(t.transaction_date),
                t.transaction_id,
                String(t.quantity),
                formatCurrency(t.invested_value),
                formatPercent(t.yield_rate)
              ]),
              theme: 'striped',
              headStyles: { fillColor: [27, 77, 62], fontSize: 8 },
              styles: { fontSize: 7 },
              margin: { left: 14 }
            });
            currentY = doc.lastAutoTable.finalY + 5;
          }
          
          // Redemption transactions
          if (txnData.redemptions && txnData.redemptions.length > 0) {
            autoTable(doc, {
              startY: currentY,
              head: [['Date', 'Transaction ID', 'Qty', 'Redemption Value', 'Yield']],
              body: txnData.redemptions.map(t => [
                formatDate(t.transaction_date),
                t.transaction_id,
                String(t.quantity),
                formatCurrency(t.redemption_value),
                formatPercent(t.yield_rate)
              ]),
              theme: 'striped',
              headStyles: { fillColor: [212, 175, 55], fontSize: 8 },
              styles: { fontSize: 7 },
              margin: { left: 14 }
            });
            currentY = doc.lastAutoTable.finalY + 5;
          }
        } catch (err) {
          /* skip this holding's transactions if fetch fails */
        }
        
        currentY += 10;
      }
      
      doc.save('BondMaster_Portfolio_' + new Date().toISOString().split('T')[0] + '.pdf');
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportToExcel = async () => {
    try {
      toast.info('Generating Excel with transactions...');
      const wb = XLSX.utils.book_new();
      
      // Holdings Summary sheet
      const holdingsData = holdings.map(h => ({ 
        'Bond ISIN': h.bond_isin, 
        'Bond Name': h.bond_name, 
        'Net Quantity': h.net_quantity, 
        'Invested Value': h.invested_value 
      }));
      const wsHoldings = XLSX.utils.json_to_sheet(holdingsData);
      XLSX.utils.book_append_sheet(wb, wsHoldings, 'Holdings Summary');
      
      // All Investment Transactions sheet
      let allInvestments = [];
      let allRedemptions = [];
      
      for (const holding of holdings) {
        try {
          const txnResponse = await axios.get(API_URL + '/api/client/transactions/' + encodeURIComponent(holding.bond_isin), getAuthHeaders());
          const txnData = txnResponse.data;
          
          if (txnData.investments) {
            txnData.investments.forEach(t => {
              allInvestments.push({
                'Bond ISIN': holding.bond_isin,
                'Bond Name': holding.bond_name,
                'Transaction Date': formatDate(t.transaction_date),
                'Transaction ID': t.transaction_id,
                'Quantity': t.quantity,
                'Invested Value': t.invested_value,
                'Yield': t.yield_rate
              });
            });
          }
          
          if (txnData.redemptions) {
            txnData.redemptions.forEach(t => {
              allRedemptions.push({
                'Bond ISIN': holding.bond_isin,
                'Bond Name': holding.bond_name,
                'Transaction Date': formatDate(t.transaction_date),
                'Transaction ID': t.transaction_id,
                'Quantity': t.quantity,
                'Redemption Value': t.redemption_value,
                'Yield': t.yield_rate
              });
            });
          }
        } catch (err) {
          /* skip this holding's transactions if fetch fails */
        }
      }
      
      if (allInvestments.length > 0) {
        const wsInvestments = XLSX.utils.json_to_sheet(allInvestments);
        XLSX.utils.book_append_sheet(wb, wsInvestments, 'Investment Transactions');
      }
      
      if (allRedemptions.length > 0) {
        const wsRedemptions = XLSX.utils.json_to_sheet(allRedemptions);
        XLSX.utils.book_append_sheet(wb, wsRedemptions, 'Redemption Transactions');
      }
      
      if (redeemedHoldings.length > 0) {
        const redeemedData = redeemedHoldings.map(h => ({ 'Bond ISIN': h.bond_isin, 'Bond Name': h.bond_name }));
        const wsRedeemed = XLSX.utils.json_to_sheet(redeemedData);
        XLSX.utils.book_append_sheet(wb, wsRedeemed, 'Fully Redeemed');
      }
      
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'BondMaster_Portfolio_' + new Date().toISOString().split('T')[0] + '.xlsx');
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const clientNavItems = useMemo(() => {
    const items = [
      { key: 'active', label: `Active Holdings (${holdings.length})`, icon: TrendingUp, testId: 'active-holdings-tab' },
      { key: 'redeemed', label: `Redeemed (${redeemedHoldings.length})`, icon: Download, testId: 'redeemed-holdings-tab' },
    ];
    if (!hideCurated) {
      items.push({ key: 'curated-bonds', label: 'Curated Bonds', icon: CheckCircle, testId: 'client-curated-bonds-tab', onSelect: () => { if (curatedBonds.length === 0) { fetchCuratedBonds(); fetchPartnerMobile(); } } });
    }
    if (isFamilyHead) {
      items.push({ key: 'family', label: 'Family', icon: Home, testId: 'client-family-tab' });
    }
    return items;
  }, [holdings.length, redeemedHoldings.length, curatedBonds.length, isFamilyHead, hideCurated, fetchCuratedBonds, fetchPartnerMobile]);

  const handleClientNav = useCallback((item) => {
    setActiveTab(item.key);
    if (item.onSelect) item.onSelect();
    setSidebarOpen(false);
  }, []);

  const handleChangePassword = async () => {
    if (!passwordForm.current) { toast.error('Please enter your current password'); return; }
    if (!passwordForm.newPass || passwordForm.newPass.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    if (passwordForm.newPass !== passwordForm.confirm) { toast.error('New passwords do not match'); return; }
    try {
      setChangingPassword(true);
      await axios.post(`${API_URL}/api/client/change-password`, { current_password: passwordForm.current, new_password: passwordForm.newPass }, getAuthHeaders());
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.card_bg }}><div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div></div>;
  }

  return (
    <div className="dashboard-container" data-testid="client-dashboard">
      {/* Mobile header */}
      <div className="mobile-header" style={{ borderColor: `${colors.primary}20` }}>
        <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <img src={logoUrl} alt="Logo" className="mobile-header-logo" />
        <div style={{ width: '2.25rem' }} />
      </div>

      {/* Sidebar overlay (mobile) */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={{ backgroundColor: colors.sidebar_bg }}>
        <div className="sidebar-logo">
          <img src={logoUrl} alt="Logo" className="h-20 max-w-[200px] object-contain" />
        </div>
        <div className="sidebar-user">
          <div className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
            <Building2 className="h-5 w-5" style={{ color: colors.primary }} />
            <div>
              <p className="text-xs" style={{ color: colors.text_secondary }}>Client</p>
              <p className="font-medium text-sm truncate" style={{ color: colors.primary }} data-testid="client-name">{user.entity_name || user.username}</p>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav" data-testid="client-sidebar-nav">
          {clientNavItems.map((item) => (
            <button
              key={item.key}
              className={`sidebar-nav-item ${activeTab === item.key ? 'active' : ''}`}
              style={{ '--nav-active-bg': colors.primary }}
              onClick={() => handleClientNav(item)}
              data-testid={item.testId}
            >
              <item.icon className="nav-icon" />{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <Button variant="outline" className="w-full justify-start text-sm h-9 mb-1.5" style={{ color: colors.primary, borderColor: `${colors.primary}30` }} onClick={() => { setShowChangePassword(true); setSidebarOpen(false); }} data-testid="change-password-btn"><KeyRound className="h-4 w-4 mr-2" />Change Password</Button>
          <Button variant="outline" className="w-full justify-start text-sm h-9" style={{ color: colors.text_secondary }} onClick={() => logout()} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign Out</Button>
        </div>
      </aside>
      <main className="main-content">
        {viewingMember ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="sm" onClick={() => { setViewingMember(null); setMemberHoldings([]); setMemberSummary(null); }} data-testid="back-from-member"><ArrowLeft className="h-4 w-4 mr-1" />Back to Family</Button>
              <h1 className="text-xl md:text-2xl font-heading font-bold" style={{ color: colors.text_primary }}>{viewingMember.client_name}'s Portfolio</h1>
            </div>
            {memberSummary && <SummaryCards summary={memberSummary} colors={colors} />}
            {memberHoldings.length > 0 && <HoldingsPieChart holdings={memberHoldings} colors={colors} />}
            <HoldingsTable holdings={memberHoldings} onViewTransactions={viewMemberTransactions} isActive={true} colors={colors} />
            <TransactionModal show={showMemberTransactions} onClose={() => setShowMemberTransactions(false)} holding={selectedMemberHolding} transactions={memberTransactions} colors={colors} onPlaceRedeemRequest={() => {}} hasEligibleTxn={false} />
          </>
        ) : (
          <>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold mb-1" style={{ color: colors.text_primary }}>{isFamilyHead ? 'Family Portfolio Dashboard' : 'Portfolio Dashboard'}</h1>
            <p style={{ color: colors.text_secondary }}>{isFamilyHead ? 'Manage your family portfolio and holdings' : 'View your bond holdings and transactions'}</p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={exportToPDF} data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-2" />PDF
            </Button>
            <Button variant="outline" size="sm" style={{ borderColor: colors.accent, color: colors.accent }} onClick={exportToExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Excel
            </Button>
          </div>
        </div>

        {isFamilyHead && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="border-0 shadow-sm" data-testid="family-total-members">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
                    <Users className="h-6 w-6" style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Total Family Members</p>
                    <p className="text-2xl font-bold" style={{ color: colors.primary }}>{families.reduce((sum, f) => sum + (f.total_members || 0), 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm" data-testid="family-total-invested">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: `${colors.accent}20` }}>
                    <Wallet className="h-6 w-6" style={{ color: colors.accent }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Total Family Invested Value</p>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{formatCurrency(families.reduce((sum, f) => sum + (f.total_invested_value || 0), 0))}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm" data-testid="family-allocation-chart">
              <CardContent className="pt-4">
                <p className="text-sm mb-1" style={{ color: colors.text_secondary }}>Family Member Allocation</p>
                <div className="h-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={familyPieData}
                        cx="50%" cy="50%" outerRadius={45} dataKey="value"
                      >
                        {familyMembersList.map((_, idx) => (
                          <Cell key={`fam-cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => formatCurrency(val)} />
                      <Legend layout="vertical" align="right" verticalAlign="middle" formatter={(value) => <span className="text-xs">{value}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <SummaryCards summary={summary} colors={colors} />
        <HoldingsPieChart holdings={holdings} colors={colors} />
        <Tabs value={activeTab || 'active'} onValueChange={setActiveTab} className="w-full">
          <TabsList className="hidden">
            {clientNavItems.map(i => <TabsTrigger key={i.key} value={i.key}>{i.label}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="active"><HoldingsTable holdings={holdings} onViewTransactions={viewTransactions} isActive={true} colors={colors} /></TabsContent>
          <TabsContent value="redeemed"><HoldingsTable holdings={redeemedHoldings} onViewTransactions={viewTransactions} isActive={false} colors={colors} /></TabsContent>
          <TabsContent value="curated-bonds">
            <Card className="border-0 shadow-sm" data-testid="client-curated-bonds-list">
              <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CheckCircle className="h-5 w-5" style={{ color: colors.accent }} />Curated Bonds</CardTitle>
                <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={fetchCuratedBonds} disabled={loadingCurated} data-testid="client-refresh-curated-bonds">
                  {loadingCurated ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}Refresh
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                      <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond ISIN</TableHead>
                      <TableHead className="font-semibold" style={{ color: colors.primary }}>Name</TableHead>
                      <TableHead className="font-semibold" style={{ color: colors.primary }}>Rating</TableHead>
                      <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Yield</TableHead>
                      <TableHead className="font-semibold" style={{ color: colors.primary }}>Maturity</TableHead>
                      <TableHead className="font-semibold" style={{ color: colors.primary }}>Int. Payout Freq.</TableHead>
                      <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Enquire</TableHead>
                      <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Image</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {curatedBonds.map((bond, idx) => (
                      <TableRow key={bond.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                        <TableCell className="font-mono text-sm">{bond.bond_isin}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate" title={bond.bond_name}>{bond.bond_name}</TableCell>
                        <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>{bond.rating || '-'}</span></TableCell>
                        <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{bond.yield_rate ? `${bond.yield_rate}%` : '-'}</TableCell>
                        <TableCell>{(bond.maturity_pct_2 || bond.maturity_pct_3) ? <MaturityBadge maturityDate={bond.maturity_date} maturityPct1={bond.maturity_pct_1} maturityDate2={bond.maturity_date_2} maturityPct2={bond.maturity_pct_2} maturityDate3={bond.maturity_date_3} maturityPct3={bond.maturity_pct_3} /> : (bond.maturity_date ? formatDate(bond.maturity_date) : '-')}</TableCell>
                        <TableCell>{bond.interest_payout_frequency || '-'}</TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" onClick={() => { setEnquiryBond(bond); setEnquiryAmount(''); setSendingEnquiry(false); setShowEnquiryDialog(true); }} data-testid={`client-enquire-${bond.bond_isin}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          {bond.bond_image_url ? (
                            <Button variant="ghost" size="sm" onClick={() => { setPreviewBondImage(bond.bond_image_url); setShowBondImagePreview(true); }} data-testid={`client-curated-view-image-${bond.bond_isin}`}>
                              <ImageIcon className="h-4 w-4" style={{ color: colors.primary }} />
                            </Button>
                          ) : <span className="text-gray-400 text-xs">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {curatedBonds.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingCurated ? 'Loading...' : 'No curated bonds available'}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          {isFamilyHead && (
            <TabsContent value="family">
              {families.map((fam) => (
                <Card key={fam.family_id} className="border-0 shadow-sm mb-4">
                  <CardHeader className="border-b flex flex-row items-center justify-between" style={{ backgroundColor: colors.card_bg }}>
                    <div>
                      <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}>
                        <Home className="h-5 w-5" />{fam.family_name}
                      </CardTitle>
                      <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{fam.total_members} members | Total: {formatCurrency(fam.total_invested_value || 0)}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Member Name</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Total Invested Value</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fam.members?.map((member) => (
                          <TableRow key={member.client_id}>
                            <TableCell className="font-medium">
                              {member.client_name}
                              {member.client_id === fam.head_client_id && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>Head</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(member.total_invested_value || 0)}</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" style={{ color: colors.primary }} onClick={() => viewMemberPortfolio(member)} data-testid={`view-member-${member.client_id}`}>
                                <Eye className="h-4 w-4 mr-1" />View Portfolio
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
              {families.length === 0 && (
                <Card className="border-0 shadow-sm"><CardContent className="text-center py-12" style={{ color: colors.text_secondary }}>No families assigned to you</CardContent></Card>
              )}
            </TabsContent>
          )}
        </Tabs>
          </>
        )}
      </main>
      <TransactionModal show={showTransactions} onClose={() => setShowTransactions(false)} holding={selectedHolding} transactions={transactions} colors={colors} onPlaceRedeemRequest={handlePlaceRedeemRequest} hasEligibleTxn={hasEligibleTxnForRedeem()} />
      {/* Redeem Request Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: colors.accent }}>
              Place Redeem Request
            </DialogTitle>
            {redeemBond && <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{redeemBond.bond_name}</p>}
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Please enter Qty</Label>
              <Input type="number" value={redeemQty} onChange={(e) => setRedeemQty(e.target.value)} placeholder="Enter quantity" className="mt-1" data-testid="client-redeem-qty-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: colors.accent }} onClick={handleSubmitRedeem} disabled={sendingRedeem || !redeemQty} data-testid="client-redeem-submit-btn">
              {sendingRedeem ? (
                <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />Sending request...</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" className="mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>Submit Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WhatsApp Enquiry Dialog */}
      <Dialog open={showEnquiryDialog} onOpenChange={setShowEnquiryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: colors.primary }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Enquire via WhatsApp
            </DialogTitle>
            {enquiryBond && <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{enquiryBond.bond_name}</p>}
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Please enter Investment amount</Label>
              <Input type="number" value={enquiryAmount} onChange={(e) => setEnquiryAmount(e.target.value)} placeholder="Enter amount in INR" className="mt-1" data-testid="client-enquiry-amount-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnquiryDialog(false)}>Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: '#25D366' }} onClick={handleEnquiry} disabled={sendingEnquiry || !enquiryAmount} data-testid="client-enquiry-submit-btn">
              {sendingEnquiry ? (
                <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />Sending enquiry...</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" className="mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>Send Enquiry</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bond Image Preview Dialog */}
      <Dialog open={showBondImagePreview} onOpenChange={setShowBondImagePreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Bond Details Image</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <img src={previewBondImage} alt="Bond Details" className="max-w-full max-h-[55vh] object-contain rounded-lg border" data-testid="client-bond-image-preview" />
            <a href={previewBondImage} download="bond_details.png" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" style={{ borderColor: colors.primary, color: colors.primary }} data-testid="client-download-bond-image-btn"><Download className="h-4 w-4 mr-2" />Download Image</Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={(open) => { setShowChangePassword(open); if (!open) setPasswordForm({ current: '', newPass: '', confirm: '' }); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: colors.primary }}><KeyRound className="h-5 w-5" />Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Current Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))} placeholder="Enter current password" data-testid="change-pwd-current" />
            </div>
            <div>
              <Label>New Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={passwordForm.newPass} onChange={(e) => setPasswordForm(prev => ({ ...prev, newPass: e.target.value }))} placeholder="Enter new password" data-testid="change-pwd-new" />
              {passwordForm.newPass && passwordForm.newPass.length < 4 && <p className="text-xs text-red-500 mt-1">Must be at least 4 characters</p>}
            </div>
            <div>
              <Label>Confirm New Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))} placeholder="Confirm new password" data-testid="change-pwd-confirm" />
              {passwordForm.confirm && passwordForm.newPass !== passwordForm.confirm && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePassword(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleChangePassword} disabled={changingPassword} data-testid="change-pwd-submit">
              {changingPassword ? 'Changing...' : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
